import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Props {
  currentLogoUrl?: string;
  onFileSelect: (file: File | null) => void;
}

export function StartupLogoUpload({ currentLogoUrl, onFileSelect }: Props) {
  const [previewUrl, setPreviewUrl] = useState(currentLogoUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    setPreviewUrl(currentLogoUrl);
  }, [currentLogoUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Logo must be less than 5MB",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);

    onFileSelect(file);
  };

  const clearSelection = () => {
    setPreviewUrl(undefined);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-4">
      {previewUrl ? (
        <div className="relative group">
          <img src={previewUrl} alt="Logo" className="h-20 w-20 object-contain rounded-lg border bg-white" />
          <button
            type="button"
            onClick={clearSelection}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="h-20 w-20 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800/50">
          <Upload className="h-8 w-8 text-muted-foreground opacity-50" />
        </div>
      )}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-2" />
          {previewUrl ? 'Change Logo' : 'Upload Logo'}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">JPG, PNG, WEBP or SVG. Max 5MB.</p>
      </div>
    </div>
  );
}
